import { Dispatch } from 'redux'
import { AccountData } from './types'
import { AccountSelectionActionTypes } from './actionTypes'
import {
  SetSelectedAccountPayload,
  SetAvailableAccountsPayload,
  UpdateAccountSelectionPayload,
} from './types'
import { RootState } from '../../../store'
import { startLoading, stopLoading } from '../loading/actions'
import { LoadingTypes } from '../loading/reducer'
import { updateAppAlert } from '../appAlert/actions'
import { RequestFailureMessage } from '../../utils/messagesContants'
import { errorLogger } from '../../utils/errorLogger'
import { AxiosError } from 'axios'
import { getErrorMessages } from '../../utils/error'
import { CloudType } from '../types'
import {
  createAccount,
  getAccounts,
  getAccountByCloudAndProduct,
  updateAccountByCloudAndProduct,
  deleteAccountByCloudAndProduct,
} from '../../api'
import { ProductType } from '../partner/reducer'
import { snakeize } from 'casing'
import { PartnerType } from '../partner/action'
import { actionTypeWrapper } from '../../utils/actionTypeWrapper'
import {
  getCachedAccountData,
  setCachedAccountData,
} from '../../utils/partnerDataCache'

export const setSelectedAccount = (
  accountId: string,
  product: ProductType,
  cloud: CloudType
): { type: string; payload: SetSelectedAccountPayload } => ({
  type: AccountSelectionActionTypes.SET_SELECTED_ACCOUNT,
  payload: { accountId, product, cloud },
})

export const setAvailableAccounts = (
  accounts: AccountData[]
): { type: string; payload: SetAvailableAccountsPayload } => ({
  type: AccountSelectionActionTypes.SET_AVAILABLE_ACCOUNTS,
  payload: { accounts },
})

export const updateAccountSelection = (
  payload: UpdateAccountSelectionPayload
): { type: string; payload: UpdateAccountSelectionPayload } => ({
  type: AccountSelectionActionTypes.UPDATE_ACCOUNT_SELECTION,
  payload,
})

export const clearAccountSelection = () => ({
  type: AccountSelectionActionTypes.CLEAR_ACCOUNT_SELECTION,
})

export const accountSelectionLoading = () => ({
  type: AccountSelectionActionTypes.ACCOUNT_SELECTION_LOADING,
})

export const accountSelectionError = (error: string) => ({
  type: AccountSelectionActionTypes.ACCOUNT_SELECTION_ERROR,
  payload: error,
})

export const cleanupAccountSelection = () => (dispatch: Dispatch) => {
  dispatch({
    type: AccountSelectionActionTypes.ACCOUNT_SELECTION_CLEANUP,
  })
}

export const loadAvailableAccounts =
  (tenantId: string, forceRefresh = false) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    if (!forceRefresh) {
      const { availableAccounts, isLoading } = getState().accountSelection
      if (isLoading) return
      if (
        availableAccounts.length > 0 &&
        availableAccounts[0]?.partnerId === tenantId
      )
        return
    }

    dispatch(startLoading(LoadingTypes.LOAD_AVAILABLE_ACCOUNTS))
    dispatch(accountSelectionLoading())
    performance.mark('loading-gate:accounts-call-start')

    try {
      // Try to get cached account data first
      const cachedAccounts = await getCachedAccountData(tenantId)

      if (cachedAccounts) {
        // Use cached data and dispatch immediately
        dispatch(setAvailableAccounts(cachedAccounts))
        performance.mark('loading-gate:accounts-loaded')

        try {
          performance.measure(
            'loading-gate:total-to-accounts-cached',
            'loading-gate:setup-start',
            'loading-gate:accounts-loaded'
          )
        } catch (_) {
          // marks may not exist if flow was skipped
        }

        // Stop loading — UI renders from cache immediately
        dispatch(stopLoading(LoadingTypes.GENERAL))

        // Schedule background refresh AFTER UI has rendered
        setTimeout(() => {
          getAccounts(tenantId)
            .then(({ data }) => {
              setCachedAccountData(tenantId, data)
              dispatch(setAvailableAccounts(data))
            })
            .catch(bgError => {
              console.warn('[AccountCache] Background refresh failed:', bgError)
            })
        }, 10000)

        return
      }

      // No cached data, fetch from API
      const { data } = await getAccounts(tenantId)

      // Cache the fresh data
      await setCachedAccountData(tenantId, data)

      dispatch(setAvailableAccounts(data))
      performance.mark('loading-gate:accounts-loaded')
      try {
        performance.measure(
          'loading-gate:total-to-accounts',
          'loading-gate:setup-start',
          'loading-gate:accounts-loaded'
        )
        performance.measure(
          'loading-gate:getUserProfile-to-accounts-gap',
          'loading-gate:api-calls-start',
          'loading-gate:accounts-call-start'
        )
      } catch (_) {
        // marks may not exist if flow was skipped
      }
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )

      dispatch(accountSelectionError(errMess))
      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.LOAD_AVAILABLE_ACCOUNTS))
    }
  }

export const selectAccount =
  (accountId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))

    try {
      const state = getState()
      const availableAccounts = state.accountSelection?.availableAccounts || []

      const account = availableAccounts.find(
        (a: AccountData) => a.id === accountId || a.partnerId === accountId
      )

      if (!account) {
        throw new Error('Selected account is not available')
      }

      const resolvedId = account.id || account.partnerId
      dispatch(setSelectedAccount(resolvedId, account.product, account.cloud))

      dispatch(
        updateAppAlert({
          message: 'Account selected successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (err) {
      const errMessage =
        err instanceof Error ? err.message : RequestFailureMessage

      dispatch(accountSelectionError(errMessage))
      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const refreshAvailableAccounts =
  (tenantId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    dispatch(accountSelectionLoading())

    try {
      dispatch(clearAccountSelection())

      await dispatch(loadAvailableAccounts(tenantId, true) as any)

      dispatch(
        updateAppAlert({
          message: 'Available accounts refreshed',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (err) {
      const errMessage =
        err instanceof Error ? err.message : RequestFailureMessage

      dispatch(accountSelectionError(errMessage))
      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const createAccountAction =
  (partnerId: string, payload: Partial<AccountData>) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    dispatch({ type: AccountSelectionActionTypes.CREATE_ACCOUNT_LOADING })

    try {
      const { data } = await createAccount(snakeize(payload))

      dispatch({
        type: AccountSelectionActionTypes.CREATE_ACCOUNT_SUCCESS,
        payload: data,
      })

      await dispatch(loadAvailableAccounts(partnerId, true) as any)

      dispatch(
        updateAppAlert({
          message: 'Account created successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )

      dispatch({
        type: AccountSelectionActionTypes.CREATE_ACCOUNT_ERROR,
        payload: errMess,
      })

      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const getAccountAction =
  (accountId: string, cloudType: CloudType, productType: ProductType) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    dispatch({ type: AccountSelectionActionTypes.GET_ACCOUNT_LOADING })

    try {
      const { data } = await getAccountByCloudAndProduct(
        accountId,
        cloudType,
        productType
      )

      dispatch({
        type: AccountSelectionActionTypes.GET_ACCOUNT_SUCCESS,
        payload: data,
      })
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )

      dispatch({
        type: AccountSelectionActionTypes.GET_ACCOUNT_ERROR,
        payload: errMess,
      })

      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const updateAccountAction =
  (
    accountId: string,
    cloudType: CloudType,
    productType: ProductType,
    payload: Partial<AccountData>
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    dispatch({
      type: AccountSelectionActionTypes.UPDATE_ACCOUNT_LOADING,
    })

    try {
      const { data } = await updateAccountByCloudAndProduct(
        accountId,
        cloudType,
        productType,
        payload
      )

      dispatch({
        type: AccountSelectionActionTypes.UPDATE_ACCOUNT_SUCCESS,
        payload: data,
      })

      // Refresh the available accounts list
      const state = getState()
      const currentAccount = state.accountSelection?.availableAccounts?.find(
        (account: AccountData) => account.id === accountId
      )

      if (currentAccount?.partnerId) {
        await dispatch(
          loadAvailableAccounts(currentAccount.partnerId, true) as any
        )
      }

      dispatch(
        updateAppAlert({
          message: 'Account updated successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )

      dispatch({
        type: AccountSelectionActionTypes.UPDATE_ACCOUNT_ERROR,
        payload: errMess,
      })

      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const deleteAccountAction =
  (accountId: string, cloudType: CloudType, productType: ProductType) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    dispatch({ type: AccountSelectionActionTypes.DELETE_ACCOUNT_LOADING })

    try {
      await deleteAccountByCloudAndProduct(accountId, cloudType, productType)

      dispatch({
        type: AccountSelectionActionTypes.DELETE_ACCOUNT_SUCCESS,
        payload: accountId,
      })

      const state = getState()
      const currentAccount = state.accountSelection?.availableAccounts?.find(
        (account: AccountData) => account.id === accountId
      )

      if (currentAccount?.partnerId) {
        await dispatch(
          loadAvailableAccounts(currentAccount.partnerId, true) as any
        )
      }

      dispatch(
        updateAppAlert({
          message: 'Account deleted successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )

      dispatch({
        type: AccountSelectionActionTypes.DELETE_ACCOUNT_ERROR,
        payload: errMess,
      })

      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
